// 🚀 ULTIMATE AUTO-SEO SYSTEM
// Just import and call initAutoSEO() - everything else is handled automatically

import { initTechnicalSEO, autoFixTechnicalSEO } from './technicalSEO';
import { performSEOAudit, autoFixSEOIssues } from './seoAudit';
import { optimizeImagesInContent } from './imageSEO';
import { analyzeContent } from './keywordOptimization';
import { autoGenerateRichSnippets } from './richSnippets';

export interface AutoSEOConfig {
  // Business info (optional)
  business?: {
    name: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    website: string;
  };
  
  // Social media (optional)
  social?: {
    twitter: string;
    facebook: string;
    linkedin: string;
  };
  
  // Analytics (optional)
  analytics?: {
    googleAnalyticsId?: string;
    googleTagManagerId?: string;
  };
  
  // Configuration
  autoFix?: boolean;
  enableLogging?: boolean;
  trackAnalytics?: boolean;
}

/**
 * 🚀 INITIALIZE ULTIMATE AUTO-SEO SYSTEM
 * 
 * Call this once in your app and SEO is handled automatically forever.
 * No configuration needed - it just works!
 */
export const initAutoSEO = (config: AutoSEOConfig = {}) => {
  const {
    autoFix = true,
    enableLogging = false, // Disable all logging in production
    trackAnalytics = true,
  } = config;

  if (enableLogging) {
    console.log('🚀 Initializing Ultimate Auto-SEO System...');
  }

  // 1. Auto-fix all SEO issues immediately
  if (autoFix) {
    const fixes = autoFixSEOIssues();
    if (fixes.length > 0 && enableLogging) {
      console.log('✅ Auto-fixed SEO issues:', fixes);
    }
  }

  // 2. Initialize technical SEO
  initTechnicalSEO({
    enforceHTTPS: true,
    securityHeaders: true,
    duplicateContentPrevention: true,
    crawlBudgetOptimization: true,
    canonicalURLs: false,
    robotsOptimization: true,
  });

  // 3. Auto-optimize page content
  const autoOptimizeContent = () => {
    const content = document.body.innerHTML;
    
    // Auto-optimize images
    const imageOptimization = optimizeImagesInContent(content);
    if (imageOptimization.changes.length > 0 && enableLogging) {
      console.log('🖼️ Auto-optimized images:', imageOptimization.changes);
    }
    
    // Auto-analyze content for SEO
    const textContent = document.body.textContent || '';
    if (textContent.length > 100) {
      const title = document.title || '';
      const keywords = extractKeywords(textContent, title);
      const contentAnalysis = analyzeContent(textContent, keywords);
      
      if (enableLogging) {
        console.log('📝 Content SEO analysis:', {
          wordCount: contentAnalysis.wordCount,
          readabilityScore: contentAnalysis.readabilityScore,
          keywordCount: contentAnalysis.keywordAnalysis.length,
          recommendations: contentAnalysis.recommendations.length,
        });
      }
    }
  };

  // 4. Auto-generate structured data
  const autoGenerateStructuredData = () => {
    const content = document.body.textContent || '';
    const title = document.title || '';
    const description = document.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    
    const schemas = autoGenerateRichSnippets(content, {
      title,
      description,
      url: window.location.href,
      type: 'website',
    });
    
    if (schemas.length > 0 && enableLogging) {
      console.log('🔍 Auto-generated structured data:', schemas.length + ' schemas');
    }
  };

  // 5. Run comprehensive SEO audit
  const runSEOAudit = () => {
    const auditResult = performSEOAudit();
    
    if (enableLogging) {
      console.log(`🎯 SEO Audit Score: ${auditResult.score}/100`);
      
      if (auditResult.issues.length > 0) {
        console.warn('⚠️ SEO Issues found:', auditResult.issues);
      }
      
      if (auditResult.recommendations.length > 0) {
        console.info('💡 SEO Recommendations:', auditResult.recommendations);
      }
    }
    
    // Track in analytics
    if (trackAnalytics && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'seo_audit_complete', {
        event_category: 'SEO',
        event_label: 'Auto-SEO Audit',
        seo_score: auditResult.score,
        issues_count: auditResult.issues.length,
      });
    }
    
    return auditResult;
  };

  // 6. Set up automatic optimization on page changes
  const setupAutoOptimization = () => {
    // Run optimization when DOM is ready
    const runOptimization = () => {
      autoOptimizeContent();
      autoGenerateStructuredData();
      runSEOAudit();
    };

    if (document.readyState === 'complete') {
      runOptimization();
    } else {
      window.addEventListener('load', runOptimization);
    }

    // Re-run on route changes (SPA support)
    let lastUrl = window.location.href;
    const observer = new MutationObserver(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        setTimeout(runOptimization, 100); // Small delay for content to load
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also listen for popstate (back/forward)
    window.addEventListener('popstate', () => {
      setTimeout(runOptimization, 100);
    });
  };

  // 7. Initialize everything
  setupAutoOptimization();

  if (enableLogging) {
    console.log('✨ Ultimate Auto-SEO System initialized successfully!');
    console.log('🎯 Your site is now automatically optimized for search engines.');
  }

  return {
    runAudit: runSEOAudit,
    optimizeContent: autoOptimizeContent,
    generateStructuredData: autoGenerateStructuredData,
  };
};

/**
 * Extract keywords from text content
 */
const extractKeywords = (content: string, title: string): string[] => {
  const text = `${title} ${content}`.toLowerCase();
  const words = text.match(/\b\w{4,}\b/g) || [];
  
  const wordCount = new Map<string, number>();
  const stopWords = new Set([
    'this', 'that', 'with', 'have', 'will', 'from', 'they', 'been', 
    'were', 'said', 'each', 'which', 'their', 'time', 'would', 'there'
  ]);
  
  words.forEach(word => {
    if (!stopWords.has(word) && word.length > 3) {
      wordCount.set(word, (wordCount.get(word) || 0) + 1);
    }
  });
  
  return Array.from(wordCount.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([word]) => word);
};

/**
 * 🎯 ONE-LINE SEO SOLUTION
 * 
 * Just call this in your main app file and forget about SEO forever!
 */
export const enableAutoSEO = () => {
  return initAutoSEO({
    autoFix: true,
    enableLogging: process.env.NODE_ENV === 'development',
    trackAnalytics: true,
  });
};

// Export for easy access
export default initAutoSEO;
